import React, { useState, useEffect, useMemo } from "react";
import { db } from "./firebase";
import { getDocs, collection, getDoc, doc } from "firebase/firestore";
import Loader from "./Loader";
import { computeProgress } from "./utils/progress";
import { ChevronUp, ChevronDown, Star, Frown, TrendingUp, HelpCircle } from "lucide-react";

const MONTHS_FULL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
];

function getPeriodBoundaries(period, year, month, wStart, wEnd) {
  if (period === "weekly") {
    const start = new Date(wStart + "T00:00");
    const end = new Date(wEnd + "T23:59:59.999");
    return { start, end };
  }
  if (period === "monthly") {
    return {
      start: new Date(year, month, 1),
      end: new Date(year, month + 1, 0, 23, 59, 59, 999)
    };
  }
  // yearly
  return {
    start: new Date(year, 0, 1),
    end: new Date(year, 11, 31, 23, 59, 59, 999)
  };
}

export default function Ranking() {
  const [goals, setGoals] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Zakres – identycznie jak w Dashboard.js
  const today = new Date();
  const [tab, setTab] = useState("weekly");
  const [wStart, setWStart] = useState(() => {
    const d = new Date(today);
    d.setDate(today.getDay() === 0 ? d.getDate() - 6 : d.getDate() - (d.getDay() - 1));
    return d.toISOString().slice(0, 10);
  });
  const [wEnd, setWEnd] = useState(today.toISOString().slice(0, 10));
  const [mIdx, setMIdx] = useState(today.getMonth());
  const [mYr, setMYr] = useState(today.getFullYear());
  const [ySel, setYSel] = useState(today.getFullYear());

  useEffect(() => {
    (async () => {
      const gSnap = await getDoc(doc(db, "goals", "currentGoals"));
      if (gSnap.exists()) setGoals(gSnap.data().goals);
      const eSnap = await getDocs(collection(db, "entries"));
      const arr = [];
      eSnap.forEach(d => arr.push({ id: d.id, ...d.data() }));
      setEntries(arr);
      setLoading(false);
    })();
  }, []);

  // Zasięg dat
  const { start, end, baseDateStr } = useMemo(() => {
    if (tab === "weekly") return { 
      start: new Date(wStart + "T00:00"), 
      end: new Date(wEnd + "T23:59:59.999"),
      baseDateStr: wEnd
    };
    if (tab === "monthly") return { 
      start: new Date(mYr, mIdx, 1), 
      end: new Date(mYr, mIdx + 1, 0, 23, 59, 59, 999),
      baseDateStr: `${mYr}-${String(mIdx + 1).padStart(2, "0")}`
    };
    return { 
      start: new Date(ySel, 0, 1), 
      end: new Date(ySel, 11, 31, 23, 59, 59, 999),
      baseDateStr: String(ySel)
    };
  }, [tab, wStart, wEnd, mIdx, mYr, ySel]);

  // RANKING – WSZYSTKIE PYTANIA, NIE TYLKO Z WYNIKIEM!
  const ranking = useMemo(() => {
    if (!goals.length) return [];
    return goals
      .filter(g => g.uid && g[tab])
      .map(g => {
        const opt = g[tab]?.opt ?? 0;
        const progress = computeProgress(entries, g, tab, baseDateStr);
        const percent = opt > 0 ? Math.max(0, Math.min(100, (progress / opt) * 100)) : 0;
        const left = opt > 0 ? Math.max(0, Number((opt - progress).toFixed(2))) : 0;
        return { 
  ...g, 
  progress: typeof progress === "number" ? Number(progress.toFixed(2)) : progress, 
  percent: Number(percent.toFixed(2)), 
  opt, 
  left 
};
      })
      .sort((a, b) => b.percent - a.percent);
  }, [goals, entries, tab, baseDateStr]);

  if (loading) return <Loader />;

  return (
    <div className="px-4 py-6 max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold text-center text-emerald-700 dark:text-emerald-300 mb-3">
        Ranking realizacji celów
      </h1>
      {/* Zakres dat jak w Dashboard */}
      <div className="mb-4 flex flex-wrap gap-3 items-center justify-center">
        <button
          className={`px-4 py-2 rounded-full border text-base font-semibold shadow-sm transition
            ${tab === "weekly"
              ? "bg-green-600 text-white border-green-600"
              : "bg-white text-green-700 border-green-200 dark:bg-gray-900 dark:text-green-200"}`}
          onClick={() => setTab("weekly")}
        >Tygodniowy</button>
        <button
          className={`px-4 py-2 rounded-full border text-base font-semibold shadow-sm transition
            ${tab === "monthly"
              ? "bg-green-600 text-white border-green-600"
              : "bg-white text-green-700 border-green-200 dark:bg-gray-900 dark:text-green-200"}`}
          onClick={() => setTab("monthly")}
        >Miesięczny</button>
        <button
          className={`px-4 py-2 rounded-full border text-base font-semibold shadow-sm transition
            ${tab === "yearly"
              ? "bg-green-600 text-white border-green-600"
              : "bg-white text-green-700 border-green-200 dark:bg-gray-900 dark:text-green-200"}`}
          onClick={() => setTab("yearly")}
        >Roczny</button>

        {tab === "weekly" && (
          <>
            <label className="ml-2 text-green-700 dark:text-green-300 font-medium">Od:</label>
            <input type="date" value={wStart} max={wEnd}
              onChange={e => setWStart(e.target.value)}
              className="border rounded px-2 py-1 dark:bg-gray-900 dark:border-green-700 dark:text-green-200"
            />
            <label className="ml-2 text-green-700 dark:text-green-300 font-medium">Do:</label>
            <input type="date" value={wEnd} min={wStart} max={today.toISOString().slice(0,10)}
              onChange={e => setWEnd(e.target.value)}
              className="border rounded px-2 py-1 dark:bg-gray-900 dark:border-green-700 dark:text-green-200"
            />
          </>
        )}
        {tab === "monthly" && (
          <>
            <label className="ml-2 text-green-700 dark:text-green-300 font-medium">Miesiąc:</label>
            <select value={mIdx} onChange={e => setMIdx(Number(e.target.value))}
              className="border rounded px-2 py-1 dark:bg-gray-900 dark:border-green-700 dark:text-green-200"
            >
              {MONTHS_FULL.map((name, idx) => (
                <option key={idx} value={idx}>{name}</option>
              ))}
            </select>
            <label className="ml-2 text-green-700 dark:text-green-300 font-medium">Rok:</label>
            <select value={mYr} onChange={e => setMYr(Number(e.target.value))}
              className="border rounded px-2 py-1 dark:bg-gray-900 dark:border-green-700 dark:text-green-200"
            >
              {Array.from({ length: 8 }, (_, i) => today.getFullYear() - 5 + i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </>
        )}
        {tab === "yearly" && (
          <>
            <label className="ml-2 text-green-700 dark:text-green-300 font-medium">Rok:</label>
            <select value={ySel} onChange={e => setYSel(Number(e.target.value))}
              className="border rounded px-2 py-1 dark:bg-gray-900 dark:border-green-700 dark:text-green-200"
            >
              {Array.from({ length: 8 }, (_, i) => today.getFullYear() - 5 + i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* RANKING */}
      <div className="mb-2 text-center text-base text-emerald-800 dark:text-emerald-200 font-semibold">
        Zakres: {start.toLocaleDateString()} – {end.toLocaleDateString()}
      </div>

      <ul className="divide-y divide-green-200 dark:divide-green-700">
        {ranking.length === 0 && (
          <li className="py-8 text-center text-gray-500 dark:text-gray-400">
            Brak celów do wyświetlenia w tym okresie.
          </li>
        )}
        {ranking.map((g, i) => (
          <li
            key={g.uid || g.id || i}
            className={`py-4 flex items-center gap-4 ${i === 0 ? "bg-green-50 dark:bg-green-900/30" : ""}`}
          >
            <span className="text-2xl font-bold w-8 text-right">{i + 1}</span>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-semibold">{g.question}</span>
                {i === 0 && (
                  <span className="inline-flex items-center px-2 py-1 ml-2 rounded-full bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-100 text-xs font-bold">
                    <Star size={16} className="mr-1" /> Lider
                  </span>
                )}
                {g.percent < 80 && (
                  <span className="inline-flex items-center px-2 py-1 ml-2 rounded-full bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-200 text-xs font-semibold">
                    <Frown size={15} className="mr-1" /> Do poprawy
                  </span>
                )}
                {g.opt === 0 && (
                  <span className="inline-flex items-center px-2 py-1 ml-2 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-200 text-xs font-semibold">
                    <HelpCircle size={15} className="mr-1" /> Brak optimum
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap mt-1">
                <span className="flex items-center gap-1 text-gray-700 dark:text-gray-300 text-sm">
                  Wynik: <span className="font-bold text-green-700 dark:text-green-300">{g.progress}</span>
                  {g.opt > 0 && (
                    <>
                      / <span className="font-bold">{g.opt}</span>
                    </>
                  )}
                  {g.opt > 0 && (
                    <span className="ml-2 text-xs text-gray-500">({Math.round(g.percent)}%)</span>
                  )}
                </span>
                {g.opt > 0 && g.left > 0 && (
                  <span className="text-xs text-yellow-700 dark:text-yellow-200">
                    Zostało: <b>{g.left}</b>
                  </span>
                )}
                {g.opt > 0 && g.left <= 0 && (
                  <span className="text-xs text-green-700 dark:text-green-200">
                    Brawo! Cel osiągnięty!
                  </span>
                )}
                {g.opt > 0 && g.progress === 0 && (
                  <span className="text-xs text-gray-400 ml-2">
                    Brak wpisów w tym okresie
                  </span>
                )}
              </div>
              <div className="w-full bg-gray-100 dark:bg-gray-700 h-3 rounded-lg overflow-hidden mt-2 mb-1">
                <div
                  className={`h-3 rounded-lg transition-all ${g.percent >= 100 ? "bg-green-500" : g.percent >= 80 ? "bg-yellow-400" : "bg-red-500"}`}
                  style={{ width: `${Math.min(g.percent, 100)}%` }}
                />
              </div>
            </div>
            <span className={`text-lg font-bold w-16 text-right`}>
              {g.opt > 0 ? Math.round(g.percent) + "%" : "-"}
            </span>
          </li>
        ))}
      </ul>

      
    </div>
  );
}
